/** Media type detection from keys/filenames and MIME types. */
import type { MediaKind } from './types.ts';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|tiff?|avif|heic|heif)$/i;
const VIDEO_EXT = /\.(mp4|mov|m4v|webm|ogg|ogv|avi|mkv|3gp)$/i;

/** Classify a key/filename by its extension. */
export function kindFromKey(key: string): MediaKind {
  if (IMAGE_EXT.test(key)) return 'image';
  if (VIDEO_EXT.test(key)) return 'video';
  return 'other';
}

export function isImageKey(key: string): boolean {
  return kindFromKey(key) === 'image';
}

export function isVideoKey(key: string): boolean {
  return kindFromKey(key) === 'video';
}

/** Classify a File by MIME first, falling back to its name extension. */
export function kindFromFile(file: File): MediaKind {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return kindFromKey(file.name);
}

export function isImageFile(file: File): boolean {
  return kindFromFile(file) === 'image';
}

/**
 * Format a byte count as a short human string.
 * Returns '' for undefined/NaN so callers can render a placeholder.
 */
export function formatSize(bytes?: number): string {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  // Whole numbers and large values render without a decimal; otherwise one place.
  const decimals = Number.isInteger(value) || value >= 100 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[i]}`;
}
